import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { meetingsAPI } from '@/lib/api';
import { toast } from 'sonner';
import {
  Video, Plus, Copy, Link2, Loader2, Users, Clock, ArrowRight, Check,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { PageTransition, FadeInItem } from '@/components/motion/Animations';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export function Meetings() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [joinCode, setJoinCode] = useState('');
  const [creating, setCreating] = useState(false);
  const [createdLink, setCreatedLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [scheduledMeetings, setScheduledMeetings] = useState([]);
  const [loadingScheduled, setLoadingScheduled] = useState(true);

  // Load only scheduled/live public meetings (thematic)
  useEffect(() => {
    (async () => {
      try {
        const res = await meetingsAPI.getMeetings({ status: 'scheduled' });
        const all = res.data || [];
        // Show only public scheduled or live meetings
        const filtered = all.filter(m => m.visibility === 'public' && (m.status === 'scheduled' || m.status === 'live'));
        setScheduledMeetings(filtered);
      } catch {}
      finally { setLoadingScheduled(false); }
    })();
  }, []);

  // Create new meeting → generate unique link
  const handleNewMeeting = async () => {
    setCreating(true);
    setCopied(false);
    try {
      const res = await meetingsAPI.createMeeting({
        title: `Sala de ${user?.name || 'Acolhimento'}`,
        topic: 'geral',
        description: 'Reunião criada via U-Clean',
        visibility: 'private',
        start_at: new Date().toISOString(),
        duration_min: 60,
      });
      const slug = res.data.slug || res.data.id;
      const link = `${window.location.origin}/reunioes/${slug}/room`;
      setCreatedLink({ link, slug, id: res.data.id });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao criar reunião');
    } finally {
      setCreating(false);
    }
  };

  // Copy link
  const handleCopyLink = async () => {
    if (!createdLink) return;
    try {
      await navigator.clipboard.writeText(createdLink.link);
      setCopied(true);
      toast.success('Link copiado!');
      setTimeout(() => setCopied(false), 3000);
    } catch {
      toast.error('Erro ao copiar');
    }
  };

  // Enter created meeting room
  const handleEnterCreated = () => {
    if (createdLink) navigate(`/reunioes/${createdLink.slug}/room`);
  };

  // Join by code or link
  const handleJoin = () => {
    const input = joinCode.trim();
    if (!input) return;

    // Extract slug from link or use directly
    let slug = input;
    if (input.includes('/reunioes/')) {
      const parts = input.split('/reunioes/');
      slug = parts[1]?.replace('/room', '').replace('/', '') || input;
    }
    navigate(`/reunioes/${slug}/room`);
  };

  const handleJoinKeyDown = (e) => {
    if (e.key === 'Enter') handleJoin();
  };

  return (
    <PageTransition>
      <div className="max-w-2xl mx-auto py-8 px-4" data-testid="meetings-page">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Video className="h-8 w-8 text-primary" strokeWidth={1.5} />
          </div>
          <h1 className="text-2xl font-bold mb-1">Reuniões</h1>
          <p className="text-sm text-muted-foreground">Salas de acolhimento e apoio mútuo</p>
        </div>

        {/* Main Actions */}
        <div className="space-y-4 mb-10">
          {/* New Meeting */}
          <Card className="border-border/50 shadow-sm" data-testid="new-meeting-section">
            <CardContent className="p-5">
              {!createdLink ? (
                <Button
                  onClick={handleNewMeeting}
                  disabled={creating}
                  className="w-full h-12 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-base gap-2"
                  data-testid="new-meeting-btn"
                >
                  {creating ? <Loader2 className="h-5 w-5 animate-spin" /> : <Plus className="h-5 w-5" />}
                  Nova Reunião
                </Button>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-foreground">Sua sala foi criada!</p>
                  <div className="flex items-center gap-2">
                    <Input
                      value={createdLink.link}
                      readOnly
                      className="flex-1 text-xs bg-muted/50 rounded-xl"
                      data-testid="created-meeting-link"
                    />
                    <Button
                      onClick={handleCopyLink}
                      variant="outline"
                      size="icon"
                      className="rounded-xl flex-shrink-0"
                      data-testid="copy-link-btn"
                    >
                      {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleEnterCreated}
                      className="flex-1 rounded-xl bg-green-600 hover:bg-green-700 text-white font-medium"
                      data-testid="enter-created-meeting-btn"
                    >
                      <Video className="h-4 w-4 mr-2" />
                      Entrar na Sala
                    </Button>
                    <Button
                      onClick={() => { setCreatedLink(null); setCopied(false); }}
                      variant="ghost"
                      className="rounded-xl text-muted-foreground"
                    >
                      Nova
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Join by code */}
          <Card className="border-border/50 shadow-sm" data-testid="join-meeting-section">
            <CardContent className="p-5">
              <p className="text-sm font-medium text-foreground mb-3">Participar de uma reunião</p>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={joinCode}
                    onChange={e => setJoinCode(e.target.value)}
                    onKeyDown={handleJoinKeyDown}
                    placeholder="Digite um código ou cole o link"
                    className="pl-10 h-11 rounded-xl bg-muted/30"
                    data-testid="join-code-input"
                  />
                </div>
                <Button
                  onClick={handleJoin}
                  disabled={!joinCode.trim()}
                  className="h-11 px-6 rounded-xl font-medium"
                  data-testid="join-meeting-btn"
                >
                  Participar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Scheduled/Thematic Meetings */}
        {!loadingScheduled && scheduledMeetings.length > 0 && (
          <div data-testid="scheduled-meetings-section">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
              Reuniões Agendadas
            </h2>
            <div className="space-y-2">
              {scheduledMeetings.map((meeting, i) => (
                <FadeInItem key={meeting.id} index={i}>
                  <ScheduledMeetingCard meeting={meeting} />
                </FadeInItem>
              ))}
            </div>
          </div>
        )}

        {/* Info text */}
        <div className="mt-10 text-center">
          <p className="text-xs text-muted-foreground/60">
            Cada sala é única e temporária. O link expira ao encerrar a reunião.
          </p>
          <p className="text-xs text-muted-foreground/60 mt-1">
            Suporte a até {300} participantes por sala.
          </p>
          <p className="text-[10px] text-muted-foreground/40 mt-3">
            Nota: O limite de 300 participantes e o tempo de partilha por participante são funcionalidades que requerem configuração backend futura.
          </p>
        </div>
      </div>
    </PageTransition>
  );
}

// Simple card for scheduled/thematic meetings
function ScheduledMeetingCard({ meeting }) {
  const navigate = useNavigate();
  const isLive = meeting.status === 'live';
  const startTime = meeting.start_at ? new Date(meeting.start_at) : null;
  const timeStr = startTime ? formatDistanceToNow(startTime, { addSuffix: true, locale: ptBR }) : '';

  return (
    <Card className="border-border/40 shadow-sm hover:shadow-md transition-shadow" data-testid={`scheduled-meeting-${meeting.id}`}>
      <CardContent className="p-4 flex items-center gap-4">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isLive ? 'bg-green-500/15' : 'bg-primary/10'}`}>
          <Video className={`h-5 w-5 ${isLive ? 'text-green-500' : 'text-primary'}`} strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <h3 className="text-sm font-semibold truncate">{meeting.title}</h3>
            {isLive && <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-green-500/20 text-green-600 dark:text-green-400 animate-pulse">AO VIVO</span>}
          </div>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            {meeting.topic && <span className="capitalize">{meeting.topic}</span>}
            {timeStr && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{timeStr}</span>}
            <span className="flex items-center gap-1"><Users className="h-3 w-3" />{meeting.participant_count || 0}</span>
          </div>
        </div>
        <Button
          onClick={() => navigate(`/reunioes/${meeting.slug || meeting.id}/room`)}
          size="sm"
          className={`rounded-xl ${isLive ? 'bg-green-600 hover:bg-green-700' : ''}`}
          data-testid={`enter-scheduled-${meeting.id}`}
        >
          Entrar <ArrowRight className="h-3.5 w-3.5 ml-1" />
        </Button>
      </CardContent>
    </Card>
  );
}
